import BaseListStore from 'stores/BaseListStore';
import IntegrationStore, { IIntegration } from 'stores/integration/IntegrationStore';
import { SortDirection } from 'constants/SortDirection';
import { action, observable, makeObservable } from 'mobx';
import { IDataFetchOptions } from '../../types';
import { IntegrationType } from 'constants/IntegrationType';
import { destroy, get, patch } from 'stores/_services/dataService';

export default class IntegrationListStore extends BaseListStore<IntegrationStore> {
    integrationIds: Record<IntegrationType, string[]> = { elastic: [], splunk: [], syslog: [], aws: [], bms: [], autotask: [] };

    constructor() {
        super({
            itemStore: IntegrationStore,
            sortBy: 'id',
            sortDirection: SortDirection.DESC
        });

        makeObservable(this, {
            integrationIds: observable,
            getIntegrations: action,
            editIntegration: action,
            deleteIntegration: action,
            toggleIntegration: action
        });
    }

    findIntegrationsForType(type: IntegrationType): IntegrationStore[] {
        return this.integrationIds[type].map((id) => this.data.get(id)) as IntegrationStore[];
    }

    async getIntegrations({ type, ...args }: IDataFetchOptions & { type: IntegrationType }): Promise<void> {
        const shouldReset = this._updateListState(args);

        const options = {
            filter: {
                where: { name: type },
                order: this.sort
            }
        };

        const { data } = await get('integrations', options);

        if (shouldReset) {
            this.clearIntegrations(type);
        }

        this._updateData(data);
        this._updateIds(data, 0, this.integrationIds[type]);
    }

    async editIntegration(integration: IIntegration): Promise<void> {
        await patch('integrations', integration);
    }

    async deleteIntegration(type: IntegrationType, id: string): Promise<void> {
        await destroy(`integrations/${id}`);

        this.clearIntegration(type, id);
    }

    async toggleIntegration(type: IntegrationType, id: string): Promise<void> {
        const integration = this.findItem(id)?.integration as IIntegration;
        const newIntegration = { ...integration, enabled: !integration.enabled };

        this._updateData(newIntegration);

        try {
            await patch('integrations', newIntegration);
        } catch (e) {
            this._updateData(integration);
        }
    }

    private clearIntegrations(type: IntegrationType) {
        this.integrationIds[type] = [];
    }

    private clearIntegration(type: IntegrationType, id: string) {
        this.integrationIds[type] = this.integrationIds[type].filter((i) => i !== id);
        this.data.delete(id);
    }
}
